import React from 'react';

// Brand-tinted skeleton primitives. Replaces ad-hoc "Loading…" pulse text
// across admin tables, panels, and KPI strips. Uses Tailwind's animate-pulse
// against the surface2 (#f5f0e8) background so placeholders sit comfortably
// inside any white card without flicker.

const BG = 'linear-gradient(90deg, #f0ebe0 0%, #f5f0e8 50%, #f0ebe0 100%)';

export function SkeletonBar({ w = '100%', h = 12, rounded = 6, className = '', style = {} }) {
  return (
    <span
      aria-hidden="true"
      className={`animate-pulse inline-block align-middle ${className}`}
      style={{ width: w, height: h, borderRadius: rounded, background: BG, ...style }}
    />
  );
}

// Generic panel skeleton — title bar + N body rows. Use inside cards/tables
// during the first load. `headerHeight` lets callers match it to a real
// section header so the swap-in doesn't shift the layout.
export function SkeletonPanel({ rows = 3, headerHeight = 14, className = '' }) {
  return (
    <div className={`p-4 ${className}`}>
      <SkeletonBar w="42%" h={headerHeight} rounded={6} />
      <div className="mt-3 space-y-2.5">
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonBar key={i} w={`${85 - (i * 7)}%`} h={11} rounded={5} />
        ))}
      </div>
    </div>
  );
}

// A single tabular row placeholder. Used by SkeletonTable, exported so callers
// with custom column widths can render directly.
export function SkeletonRow({ cols = 4, className = '' }) {
  // Vary widths so the row reads as content, not as N equal bars.
  const widths = ['28%', '40%', '22%', '34%', '18%', '52%', '24%'];
  return (
    <div className={`flex items-center gap-4 px-4 py-3 ${className}`} style={{ borderTop: '1px solid #f0ebe0' }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="flex-1">
          <SkeletonBar w={widths[i % widths.length]} h={11} rounded={5} />
        </div>
      ))}
    </div>
  );
}

// Full table skeleton: header strip + N rows. Lives inside a card-shaped
// wrapper so it doesn't need decoration from the caller.
export function SkeletonTable({ rows = 6, cols = 5, className = '' }) {
  return (
    <div
      className={`bg-white rounded-[12px] border overflow-hidden ${className}`}
      style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}
    >
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#e8e2d6', background: '#f8f5ef' }}>
        <SkeletonBar w={140} h={12} rounded={5} />
        <SkeletonBar w={80}  h={10} rounded={5} />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} cols={cols} />
      ))}
    </div>
  );
}

// Grid of KPI-card placeholders matching the Usage trend strip + Dashboard
// KpiCard footprint. `count` defaults to 6 to match the Usage screen.
export function SkeletonKpiGrid({ count = 6, className = '' }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-[12px] border p-4"
          style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}
        >
          <SkeletonBar w="55%" h={10} rounded={4} />
          <div className="mt-3">
            <SkeletonBar w="70%" h={22} rounded={6} />
          </div>
          <div className="mt-3">
            <SkeletonBar w="100%" h={20} rounded={6} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Generic card-shaped block. For the routing rail, ModelDetailPanel sections,
// etc. — anywhere a single card needs to hold its position during fetch.
export function SkeletonCard({ rows = 4, className = '', headerHeight = 14 }) {
  return (
    <div
      className={`bg-white rounded-[14px] border ${className}`}
      style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}
    >
      <SkeletonPanel rows={rows} headerHeight={headerHeight} />
    </div>
  );
}
